import { Box, Stack, Typography, useTheme } from "@mui/material";
import { PieChart } from "@mui/x-charts/PieChart";
import type { FC } from "react";
import { useFormatGoalAmount } from "web_myplann/Goals/Helpers/formatGoalAmount";
import { useGoalDetailComposition } from "web_myplann/Goals/Hooks/useGoalDetailComposition";
import type { GoalDetail } from "web_myplann/Goals/Types/GoalDetail";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { CollapsibleSection } from "web_myplann/Shared/Components/CollapsibleSection";

type Props = {
	goal: GoalDetail;
};

export const GoalDetailComposition: FC<Props> = ({ goal }) => {
	const theme = useTheme();
	const { t } = useResource();
	const formatAmount = useFormatGoalAmount();
	const compositionData = useGoalDetailComposition(goal);

	const total = compositionData.reduce((sum, s) => sum + s.value, 0);
	const remaining =
		compositionData.find((s) => s.id === "remaining")?.value ?? 0;
	const coveredPct =
		total > 0
			? Math.min(100, Math.round(((total - remaining) / total) * 100))
			: 0;

	return (
		<CollapsibleSection title={t(Resources.Goals.Detail.Composition.title)}>
			<Stack
				bgcolor={theme.palette.background.paper}
				px={1.6}
				pt={2}
				pb={2}
				alignItems="center"
			>
				<Box
					sx={{
						width: "80%",
						maxWidth: 240,
						height: 240,
						position: "relative",
					}}
				>
					<PieChart
						series={[
							{
								data: compositionData.map((d) => ({
									id: d.id,
									value: d.value,
									label: d.label,
									color: d.color,
								})),
								innerRadius: 56,
								outerRadius: 92,
								paddingAngle: 3,
								cornerRadius: 4,
							},
						]}
						hideLegend
						height={240}
					/>
					<Stack
						sx={{
							position: "absolute",
							inset: 0,
							alignItems: "center",
							justifyContent: "center",
							pointerEvents: "none",
						}}
					>
						<Typography
							variant="h1"
							color={theme.palette.primary.main}
							tabular
							sx={{ lineHeight: 1 }}
						>
							{coveredPct}%
						</Typography>
						<Typography variant="hint" mt={0.4}>
							{t(Resources.Goals.Detail.Composition.coveredLabel)}
						</Typography>
					</Stack>
				</Box>
				<Stack gap={0.8} width="100%" mt={1.6}>
					{compositionData.map((item) => (
						<Stack key={item.id} direction="row" alignItems="center" gap={1}>
							<Box
								sx={{
									width: 10,
									height: 10,
									borderRadius: "50%",
									bgcolor: item.color,
								}}
							/>
							<Typography
								variant="caption"
								color={theme.palette.grey[700]}
								flex={1}
							>
								{item.label}
							</Typography>
							<Typography
								variant="button"
								color={theme.palette.grey[900]}
								tabular
							>
								{formatAmount(item.value, goal.currencyCode)}
							</Typography>
						</Stack>
					))}
				</Stack>
			</Stack>
		</CollapsibleSection>
	);
};
